import { useCallback } from 'react';
import { submitWorkflow } from '../../../../apis/workflow/post/submitWorkflow';
import { deleteDocument } from '../../../../apis/document/delete/deleteDocument';
import { buildWorkflowSubmitPayload } from '../../../pages/RequestDetails/mappers/workflowMapper';
import { useFormContext } from '../../../../contexts/useFormContext';
import { FormState } from '@tw/tw-component-library';
import { FormSection, SectionKey } from '../../../../types';
import { getSectionFlow } from './useFormState';
import { PaymentWorkflowType } from '../types/api/graph.types';
import { getWorkflowSectionKeys } from '../../../../types';
import { isFormDataRejected } from '../../../../utils/rejectionUtils';
import { getDocumentBlobUrlsToDelete } from '../utils/documentDeletion';
import { getOvertimeSectionFlow } from '../schemas/OvertimeApproval/utils';
import {
  getGiftHospitalitySectionFlow,
  isGiftHospitalityWorkflow,
} from '../schemas/GiftHospitalityDonation/authoriserRules';

// Helper function to check if document needs uploading
const needsDocumentUpload = (document: unknown): boolean => {
  // If it's a string, it came from the server and is already uploaded
  if (typeof document === 'string') {
    return false;
  }
  // If it's an array of File objects, check if any need uploading
  if (Array.isArray(document)) {
    return document.some((doc) => doc instanceof File);
  }
  // If it's a single File object, it needs uploading
  return document instanceof File;
};

// Helper function to filter documents that need uploading
const getDocumentsToUpload = (
  documents: Record<string, unknown>
): Record<string, unknown> => {
  const documentsToUpload: Record<string, unknown> = {};

  Object.entries(documents).forEach(([fieldKey, document]) => {
    if (document !== null && needsDocumentUpload(document)) {
      documentsToUpload[fieldKey] = document;
    }
  });

  return documentsToUpload;
};

/**
 * Prepare documents for the workflow save payload.
 * File objects are uploaded separately via the document API and are omitted here.
 * Empty strings (and other non-File values) are kept so the API can clear/update associations.
 */
const getDocumentsForWorkflowSubmit = (
  documents: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!documents) return undefined;

  const result: Record<string, unknown> = {};

  Object.entries(documents).forEach(([fieldKey, document]) => {
    if (document instanceof File) return;
    if (
      Array.isArray(document) &&
      document.length > 0 &&
      document.every((item) => item instanceof File)
    ) {
      return;
    }
    result[fieldKey] = document;
  });

  return Object.keys(result).length > 0 ? result : undefined;
};
import { AuthContextProps } from '../../../../contexts/AuthContext';
import { submitDocument } from '../../../../apis/document/post/submitDocuments';
import { submitMultiDocuments } from '../../../../apis/document/post/submitMultiDocuments';

// ============= Types =============
interface UseFormHandlersProps {
  onSubmitSuccess?: () => void;
  onSubmitError?: (error: Error) => void;
  bypassSubmit?: boolean;
  mockSubmitDelay?: number;
  mockSubmitResponse?: SubmissionResponse;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

export interface SubmissionResponse {
  success: boolean;
  data?: unknown;
  error?: string;
  errors?: Record<string, string>;
}

export interface SubmittedData {
  fields?: FormSection['fields'];
  gridData?: FormSection['gridData'];
  documents?: Record<string, unknown>;
}

// ============= Constants =============
const DEFAULT_MOCK_RESPONSE: SubmissionResponse = {
  success: true,
  data: {
    message: 'Mock submission successful',
    timestamp: new Date().toISOString(),
  },
};

// ============= Helpers =============
const getNextSection = (
  currentSection: SectionKey,
  formData: Record<string, unknown> = {},
  workflowType?: PaymentWorkflowType
): SectionKey | null => {
  let currentSectionFlow: SectionKey[] =
    workflowType === PaymentWorkflowType.DubaiPaymentApproval || !workflowType
      ? getSectionFlow(formData)
      : getWorkflowSectionKeys(workflowType);

  if (workflowType === PaymentWorkflowType.OvertimeApproval) {
    currentSectionFlow = getOvertimeSectionFlow(formData) as SectionKey[];
  }

  if (isGiftHospitalityWorkflow(workflowType)) {
    currentSectionFlow = getGiftHospitalitySectionFlow(
      currentSectionFlow,
      formData
    ) as SectionKey[];
  }

  const currentIndex = currentSectionFlow.indexOf(currentSection);
  return currentIndex < currentSectionFlow.length - 1
    ? currentSectionFlow[currentIndex + 1]
    : null;
};

// ============= Hook Implementation =============
export const useFormHandlers = ({
  onSubmitSuccess,
  onSubmitError,
  bypassSubmit = false,
  mockSubmitDelay = 1000,
  mockSubmitResponse = DEFAULT_MOCK_RESPONSE,
  getAccessToken,
  spoofedUser,
}: UseFormHandlersProps) => {
  const {
    formState,
    updateFormData,
    setErrors,
    resetRejectionStatus,
    completeSection,
    submitSection,
    updateSectionState,
  } = useFormContext();

  // ============= Mock Handlers =============
  const mockSubmission = useCallback(
    async (submittedData: SubmittedData): Promise<SubmissionResponse> => {
      console.log('🔄 Mock submission initiated');
      console.log('📦 Mock submission data:', submittedData);

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (mockSubmitResponse.success) {
            console.log('✅ Mock submission successful');
            console.log('📤 Mock response:', mockSubmitResponse.data);
            resolve(mockSubmitResponse);
          } else {
            console.error('❌ Mock submission failed');
            reject(
              new Error(mockSubmitResponse.error || 'Mock submission failed')
            );
          }
        }, mockSubmitDelay);
      });
    },
    [mockSubmitDelay, mockSubmitResponse]
  );

  // ============= Form Handlers =============
  const handleSubmit = useCallback(async () => {
    console.log('🚀 Starting form submission process');
    console.log('📝 Initial form state:', formState);

    try {
      const { id } = formState.data;
      const currentAuthStage = formState.currentSection;

      // Validate required data
      if (!id || !currentAuthStage) {
        const errorMessage =
          'Missing required form data (id or currentAuthStage)';
        console.error('❌ Validation error:', errorMessage);
        setErrors({ general: errorMessage });
        onSubmitError?.(new Error(errorMessage));
        return false;
      }

      const type = formState.workflowType;

      // Form state → API section payload (fields, gridData, documents)
      const mappedData = buildWorkflowSubmitPayload(formState);
      const documents = mappedData.sections?.[0]?.documents;
      const fields = mappedData.sections?.[0]?.fields;
      const gridData = mappedData.sections?.[0]?.gridData;

      const submittedData: SubmittedData = {
        fields: fields || undefined,
        gridData: gridData || undefined,
        documents: getDocumentsForWorkflowSubmit(
          documents as Record<string, unknown> | undefined
        ),
      };

      // Handle submission
      let submitData: SubmissionResponse;
      if (bypassSubmit) {
        submitData = await mockSubmission(submittedData);
      } else {
        // Delete any documents queued for removal (e.g. invoice cleared on reject)
        const blobUrlsToDelete = getDocumentBlobUrlsToDelete(
          formState.data as Record<string, unknown> | undefined
        );
        if (blobUrlsToDelete.length > 0) {
          console.log(
            `🗑️ Deleting ${blobUrlsToDelete.length} document(s) before workflow save...`
          );
          try {
            const deleteResults = await Promise.all(
              blobUrlsToDelete.map(async (blobURL) => {
                const result = await deleteDocument({
                  blobURL,
                  getAccessToken,
                  spoofedUser: spoofedUser || null,
                });
                return { blobURL, result };
              })
            );

            const failedDeletes = deleteResults.filter(
              ({ result }) => !result.success
            );
            if (failedDeletes.length > 0) {
              const generalError =
                failedDeletes.length === 1
                  ? failedDeletes[0]?.result.error ||
                    'A document deletion failed'
                  : `${failedDeletes.length} document deletions failed`;
              console.error('❌ Document deletion failed:', failedDeletes);
              setErrors({ general: generalError });
              onSubmitError?.(new Error(generalError));
              return false;
            }

            console.log('✅ Pending document deletions completed');
            updateFormData({ pendingDocumentDeletions: [] });
          } catch (error) {
            const errorMessage = `Document deletion failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`;
            console.error('❌ Document deletion failed:', errorMessage);
            setErrors({ general: errorMessage });
            onSubmitError?.(new Error(errorMessage));
            return false;
          }
        }

        // Next, handle document uploads if they exist
        if (documents) {
          const documentsToUpload = getDocumentsToUpload(documents);
          const checkDocuments = Object.entries(documentsToUpload);
          if (checkDocuments.length > 0) {
            console.log('📄 Starting document uploads...');

            // Track upload results
            const uploadResults: Array<{
              fieldKey: string;
              success: boolean;
              error?: string;
            }> = [];

            try {
              await Promise.all(
                checkDocuments.map(async ([fieldKey, document]) => {
                  console.log(
                    `📤 Uploading document(s) for field: ${fieldKey}`
                  );

                  // Handle document upload based on single vs multiple documents
                  const documentsToUpload = Array.isArray(document)
                    ? document
                    : [document];

                  // Choose submission method based on number of documents
                  let fieldSuccess = true;
                  let fieldError: string | undefined;

                  if (documentsToUpload.length === 1) {
                    // Single document - use submitDocument
                    console.log(
                      `📤 Uploading single document for field: ${fieldKey}`
                    );

                    const result = await submitDocument({
                      workflowId: id,
                      sectionKey: currentAuthStage,
                      fieldKey: fieldKey,
                      submittedData: documentsToUpload[0],
                      getAccessToken,
                      spoofedUser: spoofedUser || null,
                    });

                    if (!result.success) {
                      fieldSuccess = false;
                      fieldError =
                        result.error ||
                        `Document upload failed for field ${fieldKey}`;
                      console.error(
                        `❌ Document upload failed for ${fieldKey}:`,
                        fieldError
                      );
                      setErrors({
                        [fieldKey]: fieldError,
                      });
                    } else {
                      console.log(
                        `✅ Single document uploaded successfully for field: ${fieldKey}`
                      );
                    }
                  } else {
                    // Multiple documents - use submitMultiDocuments
                    console.log(
                      `📤 Uploading ${documentsToUpload.length} documents for field: ${fieldKey}`
                    );

                    const result = await submitMultiDocuments({
                      workflowId: id,
                      sectionKey: currentAuthStage,
                      fieldKey: fieldKey,
                      submittedData: {
                        files: documentsToUpload,
                      },
                      getAccessToken,
                      spoofedUser: spoofedUser || null,
                    });

                    if (!result.success) {
                      fieldSuccess = false;
                      fieldError =
                        result.error ||
                        `Multiple document upload failed for field ${fieldKey}`;
                      console.error(
                        `❌ Multiple document upload failed for ${fieldKey}:`,
                        fieldError
                      );
                      setErrors({
                        [fieldKey]: fieldError,
                      });
                    } else {
                      console.log(
                        `✅ ${documentsToUpload.length} documents uploaded successfully for field: ${fieldKey}`
                      );
                    }
                  }

                  // Record the result for this field
                  uploadResults.push({
                    fieldKey: fieldKey,
                    success: fieldSuccess,
                    error: fieldError,
                  });

                  // If this field failed, we still continue with other fields
                  // The calling code will check for failures and stop the overall process
                })
              );

              // Check if any uploads failed
              const failedUploads = uploadResults.filter(
                (result) => !result.success
              );
              if (failedUploads.length > 0) {
                console.warn(
                  `⚠️ ${failedUploads.length} document upload(s) failed`,
                  failedUploads
                );
                const generalError =
                  failedUploads.length === 1
                    ? failedUploads[0]?.error || 'A document upload failed'
                    : `${failedUploads.length} document uploads failed`;
                setErrors({
                  general: generalError,
                });
                onSubmitError?.(new Error(generalError));
                return false;
              } else {
                console.log('✅ All documents uploaded successfully');
              }
            } catch (error) {
              const errorMessage = `Document upload failed: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`;
              console.error('❌ Document upload failed:', errorMessage);
              setErrors({ general: errorMessage });
              onSubmitError?.(new Error(errorMessage));
              return false;
            }
          }
        }

        // Only submit workflow if document uploads were successful (or if there were no documents)
        console.log('📋 Submitting workflow data...');

        const response = await submitWorkflow({
          id,
          sectionKey: currentAuthStage,
          type,
          submittedData,
          getAccessToken,
          spoofedUser: spoofedUser || null,
        });

        // Handle workflow submission response
        if (!response.success) {
          console.error('❌ Workflow submission failed:', response.error);

          // Use field-specific errors if available, otherwise fall back to general error
          if (response.errors && Object.keys(response.errors).length > 0) {
            console.log('📋 Setting field-specific errors:', response.errors);
            setErrors(response.errors);
          } else {
            console.log('📋 Setting general error:', response.error);
            setErrors({
              general: response.error || 'Workflow submission failed',
            });
          }

          onSubmitError?.(
            new Error(response.error || 'Workflow submission failed')
          );
          return false;
        }

        submitData = response;
        console.log('✅ Workflow submitted successfully');
      }

      // Handle successful submission
      if (submitData.success) {
        // Check if form is rejected based on form data FIRST
        const formRejected = isFormDataRejected(formState.data);

        // Check if workflow was previously rejected from API status
        const wasPreviouslyRejected =
          formState.data?.currentAuthStage === 'Rejected' ||
          formState.data?.status === 'Rejected';

        console.log('📊 Rejection status check:', {
          formRejected,
          wasPreviouslyRejected,
          currentAuthStage: formState.data?.currentAuthStage,
          status: formState.data?.status,
          isRejected: formState.isRejected,
        });

        // Handle the case where workflow was previously rejected but current submission is successful
        if (wasPreviouslyRejected && !formRejected) {
          console.log(
            '🔄 Workflow was previously rejected but current submission is successful - resetting rejection status'
          );
          console.log('📊 State before reset:', {
            isRejected: formState.isRejected,
            submittedSections: formState.submittedSections,
            sectionProgress: formState.sectionProgress,
          });
          console.log('📊 Form data rejection fields:', {
            first_approver: formState.data?.first_approver,
            resp_approve: formState.data?.resp_approve,
            second_approver: formState.data?.second_approver,
            payment_confirmation: formState.data?.payment_confirmation,
          });
          resetRejectionStatus();
        }

        // Update the rejection status immediately to ensure modal shows correct state
        // BUT: If workflow was previously rejected, don't let isFormRejected override our reset
        if (wasPreviouslyRejected) {
          console.log(
            '🔄 Workflow was previously rejected - forcing isRejected to false for resubmission'
          );
          // Use functional update to ensure we write to the root of form state
          updateFormData((prev: FormState) => ({ ...prev, isRejected: false }));
        } else if (formRejected !== formState.isRejected) {
          console.log('🔄 Updating rejection status:', {
            previous: formState.isRejected,
            current: formRejected,
          });
          // Use functional update to avoid accidentally nesting under data
          updateFormData((prev: FormState) => ({
            ...prev,
            isRejected: formRejected,
          }));
        }

        // If form is rejected AND it's not a previously rejected workflow, show rejection modal
        if (formRejected && !wasPreviouslyRejected) {
          console.log('⛔ Form is rejected. Staying on current section.');

          // Check and upload any new documents (File objects) before marking as rejected
          if (documents) {
            const documentsToUpload = getDocumentsToUpload(documents);
            const checkDocuments = Object.entries(documentsToUpload);

            if (checkDocuments.length > 0) {
              console.log('📄 Uploading new documents for rejected form...');

              // Track upload results
              const uploadResults: Array<{
                fieldKey: string;
                success: boolean;
                error?: string;
              }> = [];

              try {
                await Promise.all(
                  checkDocuments.map(async ([fieldKey, document]) => {
                    console.log(
                      `📤 Uploading document(s) for rejected form, field: ${fieldKey}`
                    );

                    // Handle document upload based on single vs multiple documents
                    const documentsToUploadArray = Array.isArray(document)
                      ? document
                      : [document];

                    // Choose submission method based on number of documents
                    let fieldSuccess = true;
                    let fieldError: string | undefined;

                    if (documentsToUploadArray.length === 1) {
                      // Single document - use submitDocument
                      console.log(
                        `📤 Uploading single document for rejected form, field: ${fieldKey}`
                      );

                      const result = await submitDocument({
                        workflowId: id,
                        sectionKey: currentAuthStage,
                        fieldKey: fieldKey,
                        submittedData: documentsToUploadArray[0],
                        getAccessToken,
                        spoofedUser: spoofedUser || null,
                      });

                      if (!result.success) {
                        fieldSuccess = false;
                        fieldError =
                          result.error ||
                          `Document upload failed for field ${fieldKey}`;
                        console.error(
                          `❌ Document upload failed for rejected form ${fieldKey}:`,
                          fieldError
                        );
                        setErrors({
                          [fieldKey]: fieldError,
                        });
                      } else {
                        console.log(
                          `✅ Single document uploaded successfully for rejected form, field: ${fieldKey}`
                        );
                      }
                    } else {
                      // Multiple documents - use submitMultiDocuments
                      console.log(
                        `📤 Uploading ${documentsToUploadArray.length} documents for rejected form, field: ${fieldKey}`
                      );

                      const result = await submitMultiDocuments({
                        workflowId: id,
                        sectionKey: currentAuthStage,
                        fieldKey: fieldKey,
                        submittedData: {
                          files: documentsToUploadArray,
                        },
                        getAccessToken,
                        spoofedUser: spoofedUser || null,
                      });

                      if (!result.success) {
                        fieldSuccess = false;
                        fieldError =
                          result.error ||
                          `Multiple document upload failed for field ${fieldKey}`;
                        console.error(
                          `❌ Multiple document upload failed for rejected form ${fieldKey}:`,
                          fieldError
                        );
                        setErrors({
                          [fieldKey]: fieldError,
                        });
                      } else {
                        console.log(
                          `✅ ${documentsToUploadArray.length} documents uploaded successfully for rejected form, field: ${fieldKey}`
                        );
                      }
                    }

                    // Record the result for this field
                    uploadResults.push({
                      fieldKey: fieldKey,
                      success: fieldSuccess,
                      error: fieldError,
                    });
                  })
                );

                // Check if any uploads failed
                const failedUploads = uploadResults.filter(
                  (result) => !result.success
                );
                if (failedUploads.length > 0) {
                  console.warn(
                    `⚠️ ${failedUploads.length} document upload(s) failed for rejected form`,
                    failedUploads
                  );
                  const generalError =
                    failedUploads.length === 1
                      ? failedUploads[0]?.error || 'A document upload failed'
                      : `${failedUploads.length} document uploads failed`;
                  setErrors({
                    general: generalError,
                  });
                  onSubmitError?.(new Error(generalError));
                  return false;
                } else {
                  console.log(
                    '✅ All new documents uploaded successfully for rejected form'
                  );
                }
              } catch (error) {
                const errorMessage = `Document upload failed for rejected form: ${
                  error instanceof Error ? error.message : 'Unknown error'
                }`;
                console.error(
                  '❌ Document upload failed for rejected form:',
                  errorMessage
                );
                setErrors({ general: errorMessage });
                onSubmitError?.(new Error(errorMessage));
                return false;
              }
            } else {
              console.log('📋 No new documents to upload for rejected form');
            }
          }

          // Mark section as submitted for rejected forms to trigger rejection modal
          submitSection(formState.currentSection);

          // Don't mark section as complete when rejected - just update the section state
          updateSectionState(
            formState.currentSection,
            formState.currentSection,
            false
          );
          return true;
        }

        // Handle previously rejected workflows that are being resubmitted successfully
        if (wasPreviouslyRejected && !formRejected) {
          console.log(
            '✅ Previously rejected workflow is being resubmitted successfully'
          );
          // Mark section as submitted to trigger completion modal
          submitSection(formState.currentSection);
        } else if (!formRejected) {
          // Only mark section as submitted if form is NOT rejected
          submitSection(formState.currentSection);
        }

        const nextSection = getNextSection(
          formState.currentSection,
          formState.data,
          type
        );

        // Update section states
        updateSectionState(formState.currentSection, nextSection, true);

        // Mark current section as complete
        completeSection(formState.currentSection);

        // Update form data
        updateFormData({
          ...formState.data,
          currentAuthStage: nextSection || formState.currentSection,
        });

        console.log('📊 Section update:', {
          previous: formState.currentSection,
          next: nextSection,
          authStage: nextSection || formState.currentSection,
          isComplete: true,
        });

        console.log('✅ Form submission completed successfully');
        onSubmitSuccess?.();
        return true;
      }

      // Handle unsuccessful submission (should not reach here due to earlier checks)
      console.log('⚠️ Submission unsuccessful - unexpected state');
      setErrors({ general: 'Submission failed unexpectedly' });
      onSubmitError?.(new Error('Submission failed unexpectedly'));
      return false;
    } catch (error) {
      console.error('❌ Submission error:', error);
      onSubmitError?.(error as Error);
      return false;
    }
  }, [
    formState,
    bypassSubmit,
    setErrors,
    resetRejectionStatus,
    updateFormData,
    onSubmitError,
    mockSubmission,
    getAccessToken,
    spoofedUser,
    updateSectionState,
    completeSection,
    submitSection,
    onSubmitSuccess,
  ]);

  const handleChange = useCallback(
    (data: Record<string, unknown>) => {
      updateFormData(data);
    },
    [updateFormData]
  );

  const handleFullStateUpdate = useCallback(
    (newState: Partial<FormState>) => {
      console.log('🔄 Updating full form state:', newState);
      updateFormData(newState);
    },
    [updateFormData]
  );

  const handleError = useCallback(
    (errors: Record<string, string>) => {
      console.log('⚠️ Setting form errors:', errors);
      setErrors(errors);
    },
    [setErrors]
  );

  // ============= Return Handlers =============
  return {
    onSubmit: handleSubmit,
    onChange: handleChange,
    onFullStateUpdate: handleFullStateUpdate,
    onError: handleError,
  };
};
